import { useTranslation } from "react-i18next";

import { cn } from "@/ui/lib/utils";
import type { ClassificationLabel, ReviewItemAlert } from "../../types/review";
import {
  deriveLabelTier,
  LABEL_TIER_STYLE,
  type LabelTier,
} from "../../utils/label-tier";

/** Tier keys are internal values, mapped to display text at the render edge. */
type LabelTierKey =
  | "labelTier.exact"
  | "labelTier.exactAlert"
  | "labelTier.strong"
  | "labelTier.weak";

const LABEL_TIER_KEYS: Record<LabelTier, LabelTierKey> = {
  exact: "labelTier.exact",
  "exact-alert": "labelTier.exactAlert",
  strong: "labelTier.strong",
  weak: "labelTier.weak",
};

interface LabelTierBadgeProps {
  label: ClassificationLabel;
  alert?: ReviewItemAlert | null;
  className?: string;
}

/**
 * Outline pill naming how much human backing a label has.
 *
 * Renders nothing for the `weak` tier — see LABEL_TIER_STYLE. Callers can
 * mount this unconditionally.
 */
const LabelTierBadge = ({ label, alert, className }: LabelTierBadgeProps) => {
  const { t } = useTranslation("mlExports");
  const tier = deriveLabelTier(label, alert);
  const style = LABEL_TIER_STYLE[tier];
  if (!style) return null;

  return (
    <span
      className={cn(
        "shrink-0 px-1 rounded border bg-background/60 text-[8px] font-bold uppercase tracking-wider leading-tight",
        style,
        className,
      )}
      title={t("labelTier.provenance", { tier: t(LABEL_TIER_KEYS[tier]) })}
    >
      {t(LABEL_TIER_KEYS[tier])}
    </span>
  );
};

export default LabelTierBadge;
